import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { GalleryHorizontalEnd, Globe, type LucideIcon } from "lucide-react";
import { Tabs } from "@/components/ui";
import { ContentPageThemeServices, FormServices } from "@/services/bedrock";
import { URLS } from "@/utils";
import { ConfigSkeleton } from "./components/LoadingStates";
import { usePreviewPlayer } from "./components/usePreviewPlayer";
import { EmbeddedHighlights } from "./EmbeddedHighlights";
import { FullReviewsPage } from "./FullReviewsPage";

type ShowcaseView = "embeddedHighlights" | "fullReviewsPage";

const VIEW_URLS: Record<ShowcaseView, string> = {
  embeddedHighlights: URLS.coach.reputation.showcaseEmbeddedHighlights,
  fullReviewsPage: URLS.coach.reputation.showcaseFullReviewsPage,
};

const VIEWS: { value: ShowcaseView; title: string; description: string; icon: LucideIcon }[] = [
  {
    value: "embeddedHighlights",
    title: "Embedded Highlights",
    description: "Featured reviews to embed on any website.",
    icon: GalleryHorizontalEnd,
  },
  {
    value: "fullReviewsPage",
    title: "Full Reviews Page",
    description: "Each Player's reviews on one shareable page.",
    icon: Globe,
  },
];

const VISIBLE_VIEW_CLASSES = "lg:flex lg:min-h-0 lg:flex-1 lg:flex-col";

export function Showcase() {
  const history = useHistory();
  const params = useParams<{ view?: string }>();
  const view: ShowcaseView = params.view === "fullReviewsPage" ? "fullReviewsPage" : "embeddedHighlights";
  const preview = usePreviewPlayer();
  const [visited, setVisited] = useState(() => new Set([view]));
  const [unsaved, setUnsaved] = useState<Record<ShowcaseView, boolean>>({
    embeddedHighlights: false,
    fullReviewsPage: false,
  });

  const setEmbeddedHighlightsUnsaved = useCallback(
    (hasChanges: boolean) => setUnsaved(current => ({ ...current, embeddedHighlights: hasChanges })),
    []
  );
  const setFullReviewsPageUnsaved = useCallback(
    (hasChanges: boolean) => setUnsaved(current => ({ ...current, fullReviewsPage: hasChanges })),
    []
  );

  useEffect(() => {
    setVisited(current => (current.has(view) ? current : new Set(current).add(view)));
  }, [view]);

  const { data: formsData, isLoading: isLoadingForms } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
  });

  const { data: themesData, isLoading: isLoadingThemes } = useQuery({
    queryKey: ["contentPageThemes", { page: 1, perPage: 100 }],
    queryFn: () => ContentPageThemeServices.getPageThemes({ page: 1, perPage: 100 }),
  });

  const forms = formsData?.content || [];
  const themes = themesData?.content || [];

  return (
    <div className="flex flex-col gap-4 lg:min-h-[46rem] lg:flex-1">
      <Tabs value={view} onValueChange={next => history.replace(VIEW_URLS[next as ShowcaseView])}>
        <Tabs.List className="grid h-auto w-full grid-cols-1 gap-3 bg-transparent p-0 sm:grid-cols-2">
          {VIEWS.map(({ value, title, description, icon: Icon }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="group/view h-auto items-start justify-start gap-3 whitespace-normal rounded-lg border-border bg-card p-4 text-left text-foreground hover:bg-muted/50 data-active:border-primary data-active:bg-primary/5 data-active:shadow-none dark:data-active:border-primary dark:data-active:bg-primary/10"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-data-active/view:bg-primary group-data-active/view:text-primary-foreground">
                <Icon className="size-4" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">{title}</span>
                <span className="text-muted-foreground text-sm font-normal">{description}</span>
              </span>
              {unsaved[value] && (
                <span className="ml-auto flex shrink-0 items-center gap-1.5 text-xs font-medium text-amber-700">
                  <span className="size-2 rounded-full bg-amber-500" aria-hidden />
                  Unsaved
                </span>
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs>

      {isLoadingForms || isLoadingThemes ? (
        <ConfigSkeleton />
      ) : (
        <>
          {visited.has("embeddedHighlights") && (
            <div className={view === "embeddedHighlights" ? VISIBLE_VIEW_CLASSES : "hidden"}>
              <EmbeddedHighlights
                themes={themes}
                forms={forms}
                preview={preview}
                onHasChangesChange={setEmbeddedHighlightsUnsaved}
              />
            </div>
          )}
          {visited.has("fullReviewsPage") && (
            <div className={view === "fullReviewsPage" ? VISIBLE_VIEW_CLASSES : "hidden"}>
              <FullReviewsPage
                themes={themes}
                forms={forms}
                preview={preview}
                onHasChangesChange={setFullReviewsPageUnsaved}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
